var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable @typescript-eslint/no-unused-vars */
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { v4 as uuidv4 } from 'uuid';
import DxSwitch from 'devextreme/ui/switch';
import { msg, updateWhenLocaleChanges } from '@lit/localize';
import { ChartFactory } from './chartFactory';
import { DateHelper } from '../../helpers';
/**
 * Represents the sparkline chart
 */
let DinapsisChart = class DinapsisChart extends LitElement {
    constructor() {
        super();
        /**
       * A property that is used to save the chart
       */
        this.chart = null;
        /**
         * A property that is used to manage class of chartFactoryHandler
         */
        this.chartFactoryHandler = new ChartFactory();
        /**
        * A property that is used to set the height
        */
        this.height = '400px';
        /**
        * A property that is used to set the width
        */
        this.width = '100%';
        /**
        * Property used to set the color of texts and icons when there isn't data.
        * Default value is #e64949.
        */
        this.colorNoData = '#e64949';
        /**
        * Property used to set an icon in the chart area when there isn't data.
        * Only accepts values from the Material iconographic library.
        * Default value is warning.
        */
        this.iconNoChartData = 'warning';
        /**
        * Property used to set a text in the chart area when there isn't data.
        * Default value is 'Gráfica sin datos'.
        */
        this.textNoChartData = '';
        /**
        * Property used to set a text in the table area when there isn't data.
        * Default value is 'Tabla sin datos'.
        */
        this.textNoTableData = '';
        /**
        * A property that is used to generate unique idChart
        */
        this.idChart = `chartdiv-${uuidv4()}`;
        /**
         * A property that is used to generate unique idLegend
         */
        this.idLegend = `ts-list-${uuidv4()}`;
        /**
        * autogenerated id for legend container
        */
        this.idLegendContainer = `legend-container-${uuidv4()}`;
        /**
         * A property that is used to generate unique idContainerChart
         */
        this.idContainerChart = `container-chartdiv-${uuidv4()}`;
        /**
         * A property that is used to generate unique idSwitch
         */
        this.idSwitch = `switch-${uuidv4()}`;
        /**
         * A private property that is used to toggle the popup
         */
        this.togglePopup = false;
        /**
        * A property that contains the error message.
        */
        this.errorMessage = '';
        /**
         * Property to control the state if the table should be displayed.
         */
        this.showTable = false;
        /**
         * Determines whether the table tab is displayed or not.
         */
        this.showChart = true;
        /**
         * Determines which chart model is active.
         */
        this.selectedOption = false;
        updateWhenLocaleChanges(this);
    }
    /**
     * This method dispatches a custom event with the name 'optionChanged'
     * and a detail object.
     * @param {any} evt - The event that triggered the method.
     * @returns {void}
     */
    emitEvent(evt) {
        const myMap = new Map([
            ['key1', 'value1'],
            ['key2', 'value2'],
        ]);
        this.selectedOption = evt.value;
        if (this.showChart === true && this.hasChartData()) {
            this.loadCharts();
        }
        this.update(myMap);
        this.updateSwitchText(evt.value);
        window.dispatchEvent(new CustomEvent('dinapsis-chart-optionChanged', { detail: this.getActiveModel() }));
    }
    /**
     * This method toggles the visibility of the download button based on whether the table
     * mode is selected.
     */
    tableModeActive() {
        const tableDiv = document.getElementById('table-mode');
        if (tableDiv) {
            const downloadBtn = document.getElementById('download-btn');
            if (tableDiv.classList.contains('selected') && this.model.allowDownload) {
                downloadBtn === null || downloadBtn === void 0 ? void 0 : downloadBtn.classList.remove('hidden');
            }
            else {
                downloadBtn === null || downloadBtn === void 0 ? void 0 : downloadBtn.classList.add('hidden');
            }
        }
    }
    /**
     * This method toggles the visibility of the legend based on the class
     * that is being used
     */
    collapseLegendContainer() {
        var _a;
        const legend = (_a = document.getElementById(this.idLegend)) === null || _a === void 0 ? void 0 : _a.parentElement;
        const iconSpan = document.getElementById('expand-span');
        if (legend) {
            if (legend.classList.contains('legendhidden')) {
                legend.classList.remove('legendhidden');
                if (iconSpan) {
                    iconSpan.textContent = 'expand_less';
                }
            }
            else {
                legend.classList.add('legendhidden');
                if (iconSpan) {
                    iconSpan.textContent = 'expand_more';
                }
            }
        }
    }
    /**
     * This method creates a div element containing various tools for the user interface.
     *
     * @returns {string} - The HTML string for the tools div.
     */
    createToolsDiv() {
        return html `
      <div class="tools">
        <div class="button ${this.showChart && !this.showTable ? 'selected' : ''}" @click="${this.handlerChart}">
          <span class="material-icons-round header-icon" title="Modo gráfica">line_axis</span>
        </div>
        <div class="button ${this.showTable && this.model.allowTable && !this.showChart ? 'selected' : ''} ${!this.model.allowTable ? 'hidden' : ''}" id="table-mode" @click="${this.handlerTable}">
          <span class="material-icons-round header-icon" title="Mostar tabla">table_chart</span>
        </div>
        ${this.createDateRange()}
      </div>
    `;
    }
    setChartHeight(height) {
        this.height = height;
    }
    /**
     * This function creates a popup div with a zoom button.
     * @returns A HTML div element with a button that has a material icon and a click event listener.
     * The visibility of the button is determined
     * by the value of the `allowPopup` property of the `model` object.
     */
    createPopupDiv() {
        return html `
      <div class="tools">
        <div class="button ${!this.model.allowPopup ? 'hidden' : ''}">
            <span class="material-icons-round header-icon" title="Zoom" @click="${this.handlerPopup}">open_in_full</span>
        </div>
      </div>
    `;
    }
    /**
     * This method creates a div element containing options for the user interface.
     *
     * @returns {string} - The HTML string for the options div, or an empty string
     * if there are no options.
     */
    createOptionDiv() {
        if (this.model.options.length === 2) {
            return html `
      <div id=${this.idSwitch} class="chart-switch">
        <span class="selected">${this.model.options[0].title}</span>
        <div class="switch" id="${this.idSwitch}"></div>
        <span>${this.model.options[1].title}</span>
      </div>
      `;
        }
        return '';
    }
    /**
     * This function creates and displays a popup window containing a chart.
     * @returns If `this.togglePopup` is true, nothing is returned and the function stops executing.
     * If `this.togglePopup` is false, the function continues to execute and creates a popup.
     */
    handlerPopup() {
        // If popup is already open don't do anything
        if (this.togglePopup)
            return;
        // Emit event active window
        window.dispatchEvent(new CustomEvent('open-popup-chart', { detail: { showTable: this.showTable, showChart: this.showChart } }));
        this.togglePopup = true;
    }
    /**
     * Handler to display the graph table
     * @param evt event.
     */
    handlerTable(evt) {
        this.activateTab(evt);
        this.showChart = false;
        if (this.showTable)
            return;
        this.showTable = true;
    }
    /**
     * Event handler to display the graph.
     * @param evt event
     */
    handlerChart(evt) {
        this.activateTab(evt);
        this.showTable = false;
        if (this.showChart)
            return;
        this.showChart = true;
    }
    /**
     * Render in html the component for the table.
     */
    createTable() {
        if (!this.showTable)
            return '';
        const textNoTableData = this.textNoTableData === '' ? msg('text_no_table_data') : this.textNoTableData;
        return html `
      <dinapsis-table-serie-chart
        .export=${this.model.allowDownload}
        .models=${this.getActiveModel()}
        .width=${this.width}
        .height=${this.height}
        .colorNoData=${this.colorNoData}
        .iconNoChartData=${this.iconNoChartData}
        .textNoTableData=${textNoTableData}
      >
      </dinapsis-table-serie-chart>`;
    }
    /**
     * Render the chart in html.
     */
    createChart() {
        if (!this.showChart)
            return '';
        return html `
      ${this.createContainerNoData()}
      <div class="row" style="display: ${this.hasChartData() ? 'block' : 'none'}">
        <div class="col chart">
          <div
            id="${this.idChart}"
            style="width: ${this.width}; height: ${this.height};">
              ${this.showErrorMessage()}
          </div>
        </div>
      </div>
      <div id="${this.idLegendContainer}" class="row" style="display: ${this.hasChartData() ? 'block' : 'none'}">
        <div class="col footer">
          <div id="legendcontainer" class="legend">
            <div id="${this.idLegend}" class="ts-list">
            </div>
            <div class="legendbutton" @click="${this.collapseLegendContainer}">
              <span class="material-icons-round expand-icon" id="expand-span">
                expand_less
              </span>
            </div>
            <div class="tooltip">
              ${this.createInfoTooltipStructure(this.setDataTooltip())}
            </div>
          </div>
        </div>
      </div>`;
    }
    /**
     * This function resizes the chart and legend containers if they exist.
     * @returns If the chart does not exist, the function returns nothing.
     */
    resizeChart() {
        // If not exist chart don't do anything
        if (!this.chart)
            return;
        // Resize chart container and legend container
        this.chart.root.resize();
        this.chart.rootLegend.resize();
    }
    /**
     * Render the DateRange in HTML.
     */
    createDateRange() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.model.showDateRange)
            return '';
        const labelTranslate = msg('date_range');
        if (labelTranslate !== 'date_range') {
            return html `
      <dinapsis-date-range
        condensed
        label=${labelTranslate}
        startDate=${(_b = (_a = this.dateRangeOptions) === null || _a === void 0 ? void 0 : _a.startDate) !== null && _b !== void 0 ? _b : DateHelper.getYearMonthDayString(new Date())}
        endDate=${(_d = (_c = this.dateRangeOptions) === null || _c === void 0 ? void 0 : _c.endDate) !== null && _d !== void 0 ? _d : DateHelper.getYearMonthDayString(new Date())}
        .intervals=${(_f = (_e = this.dateRangeOptions) === null || _e === void 0 ? void 0 : _e.intervals) !== null && _f !== void 0 ? _f : []}
        @dates-changed="${this.updateDateRange}"
      ></dinapsis-date-range>
    `;
        }
        return '';
    }
    /**
     * This function updates the date range and emits an event on the change.
     * @param {any} event - The event parameter is an object that contains information about the event
     * that triggered the function.
     */
    updateDateRange(event) {
        const { detail } = event;
        const { startDate, endDate } = detail;
        if (!this.dateRangeOptions)
            return;
        this.dateRangeOptions.startDate = startDate;
        this.dateRangeOptions.endDate = endDate;
        this.emitEventOnDateRangeChange(detail);
    }
    /**
     * This function updates the date range and emits an event on the change
     * when came it comes from the popup.
     * @param {any} event - The event parameter is an object that contains information about the event
     * that triggered the function.
     */
    updateDateRangePopup(event) {
        const { detail } = event;
        const isPopup = true;
        this.emitEventOnDateRangeChange(detail, isPopup);
    }
    /**
     * This function emits a custom event with the date range when the date range changes.
     * @param {IDateRangeEventResponse} dateRange - IDateRangeEventResponse
     * It contain startDate and endDate.
     */
    emitEventOnDateRangeChange(dateRange, isPopup = false) {
        this.dispatchEvent(new CustomEvent('date-range-change', { detail: { dateRange, isPopup } }));
    }
    /**
     * This function emits a custom event with the armchart object when the chart is loaded.
     * @return {IAmchartBase} through a custom event.
     */
    emitEventChartLoaded() {
        this.dispatchEvent(new CustomEvent('chart-loaded', { detail: this.chart }));
    }
    /**
   * The createRenderRoot function is a function that is called by the lit-html library to tell the
   * component where to render the html.
   * @returns The element itself.
   */
    createRenderRoot() {
        return this;
    }
    /**
     * If any property has changed when finish update this method is called an
     * depending on wich property has been modified,
     * it executes its update method
     * @param changedProperties - Map<string, unknown>
     */
    updated(changedProperties) {
        if (changedProperties.has('showChart') || changedProperties.has('showTable')) {
            this.handlerActiveWindows();
        }
        if (changedProperties.has('model') && this.showChart === true && this.hasChartData()) {
            this.loadCharts();
            this.createInfoTooltipStructure(this.setDataTooltip());
        }
        if (changedProperties.has('showChart') && this.showChart === true && this.hasChartData()) {
            this.loadCharts();
        }
    }
    /**
     * This function checks if both the table and chart
     * are being shown and hides the table if they are.
     */
    handlerActiveWindows() {
        if (this.showTable && this.showChart)
            this.showTable = false;
    }
    /**
     * The function activates a tab by adding a "selected" class
     * to the clicked element and removing it from any previously selected
     * element within the same parent element.
     * @param {any} evt - The `evt` parameter is an event object that is passed to the `activateTab`
     * function when it is called.
     * It contains information about the event that triggered the function,
     * such as the type of event, the target element, and any additional data related to the event. In
     * this case, the
     */
    activateTab(evt) {
        const { parentElement } = evt.currentTarget;
        const parentElementSelected = parentElement.querySelector('.selected');
        if (parentElementSelected) {
            parentElementSelected.classList.remove('selected');
        }
        evt.currentTarget.classList.add('selected');
    }
    /**
     * This method loads the charts onto the page using the AmChartsBase library.
     * It loops through the `data` property, which is an array of `IChartSeries` objects,
     * and initializes a chart for each one using the `chartFactoryHandler` object.
     */
    loadCharts() {
        var _a, _b;
        this.errorMessage = '';
        // Check if chart exists before proceeding
        if (this.chart)
            this.chart.destroy();
        try {
            // model filter
            const model = { ...this.model };
            let typeCategory = new Date();
            model.models = this.currentChartModel();
            const typeCategoryData = (_b = (_a = model.models[0]) === null || _a === void 0 ? void 0 : _a.series[0]) === null || _b === void 0 ? void 0 : _b.data[0];
            typeCategory = typeCategoryData === null || typeCategoryData === void 0 ? void 0 : typeCategoryData.category;
            if (typeCategory === undefined) {
                typeCategory = new Date();
            }
            // Create Chart Element
            this.chart = this.chartFactoryHandler.createChart(this.idChart, 'line', typeCategory, model, this.idLegend, this.chartOptions);
            model.models.forEach((data) => {
                data.series.forEach((serie) => {
                    const serieFactory = this.chartFactoryHandler.createSerie(serie, this.chart);
                    serieFactory.initSerie(this.model.enableZoom);
                });
            });
            this.setTemplateOnSamples();
            this.chart.initLegend();
            this.emitEventChartLoaded();
        }
        catch (e) {
            this.errorMessage = e.message;
            console.error(e);
        }
    }
    setDataTooltip() {
        const data = {
            icon: '',
            text: '',
            tooltipEvent: 'click',
        };
        if (data) {
            data.icon = 'info';
            data.text = this.getFilteredDataInfo(this.model.info);
            data.tooltipEvent = this.model.tooltipEvent;
        }
        return data;
    }
    /**
     * If the property icon is empty, then return an empty string.
     * Otherwise, return the html template literal with tooltip control component.
     * @param icon - String
     * @returns A string of HTML.
     */
    createInfoTooltipStructure(data, width) {
        if (data.text === '' && data.tooltipEvent === undefined)
            return '';
        return html `
      <dinapsis-icon-tooltip
        .width=${width}
        .data=${data}
      ></dinapsis-icon-tooltip>
    `;
    }
    getFilteredDataInfo(data) {
        let infoText = '';
        if (data) {
            const filteredData = data.find((element) => element.optionId === Number(this.selectedOption));
            if (filteredData) {
                infoText = filteredData.info;
            }
        }
        return infoText;
    }
    /**
   * The function returns a string with the error message in a HTML string.
   * @returns An HTML with de error message.
   */
    showErrorMessage() {
        return this.errorMessage ? html `<div>${this.errorMessage}</div>` : '';
    }
    setTemplateOnSamples() {
        var _a;
        (_a = this.chart) === null || _a === void 0 ? void 0 : _a.chart.series.values.forEach((serie) => {
            if (this.checkStrokeSettings(serie.data.values)) {
                serie.strokes.template.setAll({
                    templateField: 'strokeSettings',
                    strokeWidth: 2,
                });
            }
            if (this.checkFillSettings(serie.data.values)) {
                serie.fills.template.setAll({
                    visible: true,
                    fillOpacity: 1,
                    templateField: 'fillSettings',
                });
            }
        });
    }
    checkStrokeSettings(data) {
        if (data.some((element) => (element === null || element === void 0 ? void 0 : element.strokeSettings)
            && Object.keys(element.strokeSettings).length !== 0)) {
            return true;
        }
        return false;
    }
    checkFillSettings(data) {
        if (data.some((element) => (element === null || element === void 0 ? void 0 : element.fillSettings)
            && Object.keys(element.fillSettings).length !== 0)) {
            return true;
        }
        return false;
    }
    /**
   * This method change class selected to switch options
   * @param switchValue Boolena switch value.
   */
    updateSwitchText(switchValue) {
        const options = document.querySelectorAll(`#${this.idSwitch} span`);
        if (!switchValue) {
            options[1].classList.remove('selected');
            options[0].classList.add('selected');
        }
        else {
            options[0].classList.remove('selected');
            options[1].classList.add('selected');
        }
    }
    /**
     * The above function is a lifecycle hook that is called when the component is first updated.
     * It is used to initialize the chart.
     */
    firstUpdated() {
        const container = document.querySelector(`#${this.idSwitch} > .switch`);
        const that = this;
        if (container) {
            const switchOption = new DxSwitch(container, {
                onValueChanged(e) {
                    that.emitEvent(e);
                },
            });
            if (switchOption) {
                switchOption.option('option', this.selectedOption);
            }
            else {
                throw new Error('No se ha podido declarar el objeto switch');
            }
        }
    }
    /**
    * The function returns a string of HTML that contains a div that contains the component.
    * @returns The return statement is returning the html template literal
    */
    render() {
        return html `
    <div class="row dinapsis-chart" id="${this.idContainerChart}">
      <div class="col">
        <div class="row">
          <div class="col header">
            ${this.createToolsDiv()}
            <div class="container-tools-right">
              ${this.createPopupDiv()}
              ${this.createOptionDiv()}
            </div>
          </div>
        </div>
      </div>
      ${this.createChart()}
    </div>
    ${this.createPopup()}
    ${this.createTable()}
  `;
    }
    /**
     * This function toggles a popup based on an event detail.
     */
    togglePopupHandler(event) {
        const { detail } = event;
        this.togglePopup = detail.isOpened;
        if (!this.togglePopup)
            this.restoreModelPopup();
    }
    /**
     * The function restores the modelPopup object by copying the values from the model object.
     */
    restoreModelPopup() {
        this.modelPopup = structuredClone(this.model);
    }
    /**
     * This function creates a popup chart using a cloned model and event handlers.
     * @returns The `createPopup()` function is returning an HTML template literal that creates a
     * `dinapsis-popup-chart` custom element with the following properties and event listener:
     */
    createPopup() {
        if (!this.model.allowPopup)
            return '';
        if (!this.modelPopup)
            this.restoreModelPopup();
        const dateRangeOptionsClone = structuredClone(this.dateRangeOptions);
        return html `<dinapsis-popup-chart .togglePopup="${this.togglePopup}" @date-range-change=${this.updateDateRangePopup} .model=${this.modelPopup} .dateRangeOptions="${dateRangeOptionsClone}" @update-toggle=${this.togglePopupHandler}></dinapsis-popup-chart>`;
    }
    /**
     * Get active model.
     * @returns @see IChartDataModel[]
     */
    getActiveModel() {
        return this.model.models.filter((m) => m.optionId === Number(this.selectedOption).toString());
    }
    currentChartModel() {
        if (this.model.options.length > 0) {
            const modelFilter = this.getActiveModel();
            return modelFilter;
        }
        if (this.model.models.length > 0) {
            const [first] = this.model.models;
            return [first];
        }
        return [];
    }
    hasChartData() {
        const model = this.currentChartModel();
        if (!Array.isArray(model))
            return false;
        return model.some((serie) => serie.series.some((data) => Array.isArray(data.data) && data.data.some((point) => point.value !== undefined
            || (Array.isArray(point.value) && point.value.length > 0))));
    }
    createContainerNoData() {
        if (this.hasChartData())
            return '';
        const textNoChartData = this.textNoChartData === '' ? msg('text_no_chart_data') : this.textNoChartData;
        return html `
      <div class="row">
        <div class="col chart">
          <div class='no-chart-data container-no-data' style="color: ${this.colorNoData}; width: ${this.width}; height: ${this.height}; display: flex; justify-content: center; align-items: center;">
            <span class="material-icons-round no-data-icon">${this.iconNoChartData}</span>
            <span class="no-data-text">${textNoChartData}</span>
          </div>
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: Object })
], DinapsisChart.prototype, "model", void 0);
__decorate([
    property({ type: Object })
], DinapsisChart.prototype, "modelPopup", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "height", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "width", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "colorNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "iconNoChartData", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "textNoChartData", void 0);
__decorate([
    property({ type: String })
], DinapsisChart.prototype, "textNoTableData", void 0);
__decorate([
    state(),
    property({ type: Boolean })
], DinapsisChart.prototype, "togglePopup", void 0);
__decorate([
    state(),
    property({ type: String })
], DinapsisChart.prototype, "errorMessage", void 0);
__decorate([
    property({ type: Object })
], DinapsisChart.prototype, "chartOptions", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisChart.prototype, "showTable", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisChart.prototype, "showChart", void 0);
__decorate([
    property({ type: Object })
], DinapsisChart.prototype, "dateRangeOptions", void 0);
DinapsisChart = __decorate([
    customElement('dinapsis-chart')
], DinapsisChart);
export { DinapsisChart };
//# sourceMappingURL=dinapsis-chart.js.map